// @booking/lib/bookingConfig.ts
import type { BookingConfig, BookingContextKey, StageId } from '@booking/types/bookingConfig'

const CONFIGS: Record<BookingContextKey, BookingConfig> = {
  default: {
    context: 'default',
    ticketTypes: ['adult', 'child', 'senior'],
    maxTicketsPerBooking: 9,
    allowDiscountCode: true,
    stages: ['quantity', 'datetime', 'customer', 'addons', 'payment', 'thankyou', 'associate'],
    navStages: ['quantity', 'datetime', 'customer', 'addons', 'payment'],
    ticketPriceFunc: (price) => price + 3,
    text: {
      ticketTypeLabelKey: 'ticket.summaryBox.typeStandard',
    },
    addons: {
      titleKey: 'addons.title',
      subtitleKey: 'addons.subtitle',
      items: ['insurance'],
    },
  },

  voucher: {
    context: 'voucher',
    ticketTypes: [],
    maxTicketsPerBooking: 9,
    allowDiscountCode: false,
    text: {
      ticketTypeLabelKey: 'ticket.summaryBox.typeVoucher',
    },
    stages: ['voucher', 'customer', 'payment', 'thankyou'],
    navStages: ['voucher', 'customer', 'payment'],
  },

  'rock-in-rio': {
    context: 'rock-in-rio',
    ticketTypes: ['adult-rir', 'child-rir', 'senior-rir'],
    maxTicketsPerBooking: 9,
    allowDiscountCode: false,
    stages: ['extras', 'quantity', 'datetime', 'customer', 'payment', 'thankyou', 'associate'],
    navStages: ['extras', 'quantity', 'datetime', 'customer', 'payment'],
    ticketPriceFunc: (price) => Math.round((price / 0.85) * 2) / 2, // round in 0.5 increments
    text: {
      ticketTypeLabelKey: 'rockInRio.ticketType',
      quantityDesc2Key: 'rockInRio.ticketQuantityDesc',
      helpBoxKey: 'rockInRio.helpBox',
    },
    additionalTerms: [
      {
        key: 'rockInRio.terms',
        components: { link: 'https://rockinriolisboa.pt/pt/termos-e-condicoes' },
      },
    ],
    extras: {
      component: 'rock-in-rio',
      maxExtrasTotal: 9,
      titleKey: 'extras.rockInRio.title',
      subtitleKey: 'extras.rockInRio.subtitle',
      navLabelKey: 'extras.rockInRio.navLabel',
      summaryLabelKey: 'extras.rockInRio.summaryLabel',
      items: [
        { category: 'rir-day20', labelKey: 'extras.rockInRio.day1', price: 89 },
        { category: 'rir-day21', labelKey: 'extras.rockInRio.day2', price: 89 },
        { category: 'rir-day27', labelKey: 'extras.rockInRio.day3', price: 89 },
        { category: 'rir-day28', labelKey: 'extras.rockInRio.day4', price: 89 },
      ],
    },
  },

  'happy-hour': {
    context: 'happy-hour',
    ticketTypes: ['adult-hh', 'child-hh'],
    maxTicketsPerBooking: 6,
    allowDiscountCode: false,
    rules: {
      'adult-hh': { min: 1, max: 1 },
      'child-hh': { min: 2, max: 4 },
    },
    defaultQuantities: [
      { category: 'adult-hh', quantity: 1 },
      { category: 'child-hh', quantity: 2 },
    ],
    text: {
      ticketTypeLabelKey: 'ticket.summaryBox.typeHappyHour',
    },
    sessionTags: ['Happy Hour'],
    stages: ['quantity', 'datetime', 'customer', 'payment', 'thankyou', 'associate'],
    navStages: ['quantity', 'datetime', 'customer', 'payment'],
  },
}

export function resolveBookingConfig(key: string | null | undefined): BookingConfig {
  const k = (key ?? 'default') as BookingContextKey
  return CONFIGS[k] ?? CONFIGS.default
}

/** Returns the 1-based stage number for a stageId in the config's pipeline. */
export function stageNumberOf(config: BookingConfig, stageId: StageId): number {
  const idx = config.stages.indexOf(stageId)
  return idx === -1 ? 1 : idx + 1
}

/** Returns the StageId at a 1-based stage number, or null if out of range. */
export function stageIdOf(config: BookingConfig, stage: number): StageId | null {
  return config.stages[stage - 1] ?? null
}

/** Returns the 1-based stage number that follows stageId in the pipeline. */
export function nextStageOf(config: BookingConfig, stageId: StageId): number {
  const idx = config.stages.indexOf(stageId)
  if (idx === -1 || idx + 1 >= config.stages.length) return stageNumberOf(config, stageId)
  return idx + 2
}

/**
 * Strips flow-specific suffixes from a ticket variant so it resolves to the
 * correct shared translation key.  e.g. 'adult-rir' → 'adult'.
 * '-hh' variants are intentionally left intact (they have their own translations).
 */
export function variantTranslationKey(variant: string): string {
  return variant.replace(/-rir$/, '')
}
